<template>
  <article class="CardTripHistory">
    <!-- Image/color panel: grid-area image -->
    <div class="CardTripHistory-imageContainer">
      <Tag
        v-if="timeStatus"
        size="sm"
        class="CardTripHistory-statusTag"
        type="information"
        variant="filled"
      >
        {{ t(`ui.components.cardTripHistory.status.${timeStatus}`) }}
      </Tag>
      <img
        class="CardTripHistory-image"
        :src="imageSrc"
        alt=""
        loading="lazy"
      >
    </div>

    <!-- Destination: grid-area dest -->
    <h4 class="CardTripHistory-destination">
      {{ destination }}
    </h4>

    <!-- Single PNR: grid-area pnr — repositioned via CSS Grid per breakpoint -->
    <span v-if="reference" class="CardTripHistory-pnr">
      <CopyClipboard
        :value="reference"
        size="sm"
        @copy="emit('copyClipboard', true)"
        @error="emit('copyClipboard', false)"
      />
    </span>

    <!-- Info rows: grid-area body -->
    <div class="CardTripHistory-body">
      <div class="CardTripHistory-row">
        <Icon icon="ticket-confirmation" size="sm" />
        <span class="CardTripHistory-text">{{ tripTypeLabel }}:</span>
        <span class="CardTripHistory-route">
          <span class="CardTripHistory-text">{{ departureCode }}</span>
          <Icon icon="arrow-top-left-bottom-right" size="sm" />
          <span class="CardTripHistory-text">{{ arrivalCode }}</span>
        </span>
      </div>

      <div class="CardTripHistory-row">
        <Icon icon="calendar" size="sm" />
        <span class="CardTripHistory-text">{{ formattedIntervalDates }}</span>
      </div>

      <div class="CardTripHistory-row">
        <Icon icon="account" size="sm" />
        <span class="CardTripHistory-text">{{ t('ui.components.cardTripHistory.passengers', passengers) }}</span>
      </div>

      <div class="CardTripHistory-row">
        <Icon icon="cached" size="sm" />
        <span class="CardTripHistory-text">{{ t('ui.components.cardTripHistory.redemptionDate', { date: formattedRedemptionDate }) }}</span>
      </div>
    </div>

    <!-- CTAs: grid-area ctas -->
    <div class="CardTripHistory-ctas">
      <ActionButton
        variant="primary"
        size="sm"
        :rightIcon="isUpcoming ? 'open-in-new' : undefined"
        @click="emit('clickPrimaryCta')"
      >
        {{ primaryCtaLabel }}
      </ActionButton>
      <ActionButton
        variant="secondary"
        size="sm"
        @click="emit('clickSecondaryCta')"
      >
        {{ t('ui.components.cardTripHistory.cta.seeDetails') }}
      </ActionButton>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import { useDateFormat } from '@cvo/ui/composables/useDateFormat';
import { useImage } from '@vueuse/core';
import { TripCardStatuses, normalizeTimezone } from '@cvo/domain';
import ActionButton from '../../atoms/ActionButton/ActionButton.vue';
import Icon from '../../atoms/Icon/Icon.vue';
import CopyClipboard from '../../molecules/CopyClipboard/CopyClipboard.vue';
import Tag from '../../atoms/Tag/Tag.vue';
import { type CardTripHistoryProps, type CardTripHistoryEmits } from './CardTripHistory.model';

const props = defineProps<CardTripHistoryProps>();
const emit = defineEmits<CardTripHistoryEmits>();
const { t } = useI18n();

const { error: imageError } = useImage(() => ({ src: props.imageUrl ?? '' }));
const imageSrc = computed(() => {
  return  !imageError.value && props.imageUrl ? props.imageUrl : t('airports.default.image');
});

const tripTypeLabel = computed(() => t(`ui.global.tripType.${props.tripType}`));
const isUpcoming = computed(() => props.timeStatus === TripCardStatuses.upcoming);
const primaryCtaLabel = computed(() => {
  return isUpcoming.value
    ? t('ui.components.cardTripHistory.cta.manageBooking')
    : t('ui.components.cardTripHistory.cta.bookAgain');
});

const formattedRedemptionDate = useDateFormat(() => new Date(props.redemptionDate));
const formattedDepartDate = useDateFormat(
  () => new Date(props.departDate),
  () => ({ timeZone: normalizeTimezone(props.departureTimezone) }),
);
const formattedArrivalDate = useDateFormat(
  () => new Date(props.arrivalDate),
  () => ({ timeZone: normalizeTimezone(props.arrivalTimezone) }),
);

const formattedIntervalDates = computed(() => {
  const parts = [formattedDepartDate.value, formattedArrivalDate.value].filter(Boolean);
  return parts.join(t('ui.components.cardTripHistory.separator'));
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/utils/mixins';
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-ui {
  .CardTripHistory {
    background: vars.$organisms-card-triphistory-bg-color;
    border: vars.$organisms-card-triphistory-border-stroke solid vars.$organisms-card-triphistory-border-color;
    border-radius: vars.$organisms-card-triphistory-sm-border-radius;
    overflow: hidden;
    // CSS Grid: PNR repositioned via named areas — no duplicate instances needed
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-areas:
      "image image"
      "dest  pnr"
      "body  body"
      "ctas  ctas";

    @include responsive.desktop {
      border-radius: vars.$organisms-card-triphistory-lg-md-border-radius;
      grid-template-columns: minmax(100px, 240px) 1fr auto;
      grid-template-areas:
        "image dest pnr"
        "image body ctas";
    }

    &-imageContainer {
      grid-area: image;
      position: relative;
      background-color: vars.$color-neutral-gray-100;
      display: flex;
      align-items: flex-start;
      padding: vars.$organisms-card-triphistory-sm-padding-top-bottom vars.$organisms-card-triphistory-sm-padding-left-right;
      min-block-size: vars.$dimensions-d-20;

      @include responsive.desktop {
        padding: vars.$organisms-card-triphistory-lg-md-padding-top-bottom vars.$organisms-card-triphistory-lg-md-padding-left-right;
      }
    }

    &-statusTag {
      z-index: 1;
    }

    &-image {
      @include layout.square(100%);
      position: absolute;
      inset: 0;
      object-fit: cover;
    }

    &-destination {
      grid-area: dest;
      color: vars.$organisms-card-triphistory-enabled-title-color;
      padding: vars.$organisms-card-triphistory-sm-padding-top-bottom vars.$organisms-card-triphistory-sm-padding-left-right 0;
      @include text.heading-responsive(h6, h4);

      @include responsive.desktop {
        padding: vars.$organisms-card-triphistory-lg-md-padding-top-bottom vars.$organisms-card-triphistory-lg-md-padding-left-right 0;
      }
    }

    &-pnr {
      grid-area: pnr;
      align-self: center;
      padding: vars.$organisms-card-triphistory-sm-padding-top-bottom vars.$organisms-card-triphistory-sm-padding-left-right 0 0;

      @include responsive.desktop {
        display: block;
        text-align: right;
        padding: vars.$organisms-card-triphistory-lg-md-padding-top-bottom vars.$organisms-card-triphistory-lg-md-padding-left-right 0;
      }
    }

    &-body {
      grid-area: body;
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-2;
      padding: vars.$dimensions-d-3 vars.$organisms-card-triphistory-sm-padding-left-right vars.$organisms-card-triphistory-sm-padding-top-bottom;

      @include responsive.desktop {
        padding: vars.$dimensions-d-3 vars.$organisms-card-triphistory-lg-md-padding-left-right vars.$organisms-card-triphistory-lg-md-padding-top-bottom;
      }
    }

    &-ctas {
      grid-area: ctas;
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-3;
      padding: 0 vars.$organisms-card-triphistory-sm-padding-left-right vars.$organisms-card-triphistory-sm-padding-top-bottom;
      min-width: 180px;

      @include responsive.desktop {
        padding: 0 vars.$organisms-card-triphistory-lg-md-padding-left-right vars.$organisms-card-triphistory-lg-md-padding-top-bottom;
        margin-top: vars.$dimensions-d-3;
      }
    }

    &-row {
      display: flex;
      gap: vars.$dimensions-d-2;
      color: vars.$organisms-card-triphistory-enabled-text-color;
    }

    &-text {
      @include text.text('xs');
    }

    &-route {
      gap: vars.$dimensions-d-1;
      display: flex;

      @include mixins.rtl {
        flex-direction: row-reverse;

        .Icon {
          display: flex;
          transform: rotate(180deg);
        }
      }
    }
  }
}
</style>
